import { futureSummary, editResult, selectedHistory, conversionObservation } from "./teamAnalysis";
const fields = ["home_for", "home_against", "away_for", "away_against"];
// Explicit, conservative editing aid, not a model update or a fitted forecast.
export function statisticSuggestion(
  observations,
  teamId,
  statisticId,
  field,
  stored,
  stronger = false
) {
  const venue = field.startsWith("home") ? "home" : "away",
    own = field.endsWith("for");
  const unit = (v) => (Number(statisticId) === 45 && v > 1 ? v / 100 : v);
  const pairs = observations
    .filter((f) => f.final && Number(f[venue + "_team_id"]) === Number(teamId))
    .flatMap((f) => {
      const side = own ? venue : venue === "home" ? "away" : "home";
      const a = statisticId === "conversion" ? f.conversion?.actual?.[side]?.rate : f.statistics?.[statisticId]?.[side],
        e = statisticId === "conversion" ? f.conversion?.expected?.[side]?.rate : f.statistics_model?.expected?.[statisticId]?.[side];
      return Number.isFinite(a) && Number.isFinite(e) && e > 0
        ? [[unit(a), unit(e)]]
        : [];
    });
  if (!pairs.length || !Number.isFinite(stored) || stored <= 0) return null;
  const observed = pairs.reduce((s, p) => s + p[0], 0) / pairs.length;
  const expected = pairs.reduce((s, p) => s + p[1], 0) / pairs.length;
  const weight = pairs.length / (pairs.length + (stronger ? 6 : 12)),
    limit = stronger ? 0.4 : 0.2;
  const shift = Math.max(
    -limit,
    Math.min(limit, weight * (observed / expected - 1))
  );
  const value = Math.max(
    0,
    Math.min(Number(statisticId) === 45 ? 1 : Infinity, stored * (1 + shift))
  );
  return { value, count: pairs.length, observed, expected, weight, limit };
}
export function teamSuggestions(data, ids, stronger = false, lastSix = false) {
  return ids.flatMap((id) => {
    const games = selectedHistory(data.observations, id, lastSix);
    return Object.entries(data.state?.teams?.[id]?.statistics || {}).flatMap(
      ([statistic_id, values]) =>
        fields.flatMap((field) => {
          const p = statisticSuggestion(
            games,
            id,
            statistic_id,
            field,
            values[field],
            stronger
          );
          return p && Math.abs(p.value - values[field]) > 1e-12
            ? [
                {
                  team_id: Number(id),
                  group: "statistic",
                  statistic_id,
                  field,
                  value: p.value,
                },
              ]
            : [];
        })
    );
  });
}
export function neutralResult(teams, id, quotient, goals) {
  const rows = Object.values(teams).map(futureSummary).filter(Boolean);
  if (!rows.length || !teams[id]) throw Error("Missing team settings");
  const base = { ...teams[id] };
  for (const key of [
    "for_home",
    "against_home",
    "for_away",
    "against_away",
    "draw_factor",
  ]) {
    if (rows.some((r) => !Number.isFinite(r[key])))
      throw Error("Incomplete league settings");
    base[key] = rows.reduce((sum, r) => sum + r[key], 0) / rows.length;
  }
  return editResult(
    editResult(futureSummary(base), "total_strength", quotient),
    "total_goals",
    goals
  );
}

// A transparent editing range, not a confidence interval or automatic update.
// Anchor to saved settings, never repeatedly add the same evidence to a draft.
export function statisticAdjustmentRange(observations, teamId, statisticId, field, stored) {
  if(!Number.isFinite(stored)||stored<0)return null;
  const venue=field.startsWith('home')?'home':'away',side=field.endsWith('for')?venue:venue==='home'?'away':'home';
  const unit=v=>Number(statisticId)===45&&v>1?v/100:v;
  const paired=observations.filter(f=>{
    if(!f.final||Number(f[venue+'_team_id'])!==Number(teamId))return false;
    if(statisticId==='conversion')return ['actual','expected'].every(k=>{const v=f.conversion?.[k]?.[side];return Number.isFinite(v?.goals)&&v.goals>=0&&Number.isFinite(v?.moments)&&v.moments>0;});
    return [f.statistics?.[statisticId]?.[side],f.statistics_model?.expected?.[statisticId]?.[side]].every(v=>Number.isFinite(v)&&v>=0);
  });
  if(!paired.length)return null;
  const observed=statisticId==='conversion'?conversionObservation(paired,teamId,field).mean:paired.reduce((s,f)=>s+unit(f.statistics[statisticId][side]),0)/paired.length;
  const expected=statisticId==='conversion'?conversionObservation(paired,teamId,field,true).mean:paired.reduce((s,f)=>s+unit(f.statistics_model.expected[statisticId][side]),0)/paired.length;
  const endpoint = Math.max(statisticId === "conversion" ? Number.EPSILON : 0,
    Math.min(Number(statisticId) === 45 ? 1 : Infinity, stored + observed - expected));
  return {observed,expected,count:paired.length,stored,endpoint,midpoint:(stored+endpoint)/2,
    min:Math.min(stored,endpoint),max:Math.max(stored,endpoint)};
}
